import { TextField, type SxProps, type TextFieldProps, type Theme } from '@mui/material';
import { forwardRef, useId, type ComponentProps } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Textarea as FplTextarea, Label as FplLabel } from '@figma/fpl-components';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { extractSxWidth, toCssWidth, toStringValue } from '@/UI/Input/utils';

type TextAreaSx = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;

const defaultSx: TextAreaSx = {
  mt: 1,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: color.White08_T,
    },
    '&:hover fieldset': {
      borderColor: color.White08_T,
    },
    '&.Mui-focused fieldset': {
      borderColor: color.White40_T,
      borderWidth: 1,
    },
    '&.Mui-disabled fieldset': {
      borderColor: color.White08_T,
    },
    '&.Mui-error': {
      backgroundColor: color.Weavy_Error_08_T,
      '& fieldset': {
        borderColor: color.Weavy_Error_64_T,
      },
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: color.White08_T,
    WebkitTextFillColor: color.White08_T,
  },
};

const DEFAULT_ROWS = 8;

export type TextAreaProps = Omit<TextFieldProps, 'multiline'>;

type FplTextareaProps = ComponentProps<typeof FplTextarea>;
// MUI types these DOM handlers for the field root (HTMLDivElement) while FPL types them for the
// textarea; at runtime they receive the real textarea event. This is the one adapter boundary
// cast, scoped to just the passthrough handlers so the rest of `fplProps` stays type-checked.
type DomHandlers = Pick<FplTextareaProps, 'onFocus' | 'onBlur' | 'onKeyDown' | 'onPaste' | 'onMouseDown'>;

/**
 * Legacy MUI implementation. Rendered when the `fpl-migration` flag is off so the flag-off
 * path stays byte-for-byte identical for rollback safety.
 */
const LegacyTextArea = forwardRef<HTMLDivElement, TextAreaProps>(function LegacyTextArea(
  { fullWidth = true, size = 'small', rows = DEFAULT_ROWS, slotProps, sx, inputProps, ...props },
  ref,
) {
  const mergedInputProps = {
    ...inputProps,
    style: { resize: 'vertical' as const, ...inputProps?.style },
  };

  return (
    <TextField
      ref={ref}
      multiline
      fullWidth={fullWidth}
      size={size}
      rows={props.minRows === undefined && props.maxRows === undefined ? rows : undefined}
      slotProps={slotProps}
      inputProps={mergedInputProps}
      sx={{ ...defaultSx, ...sx }}
      {...props}
    />
  );
});

const styles = stylex.create({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
  },
  fieldDefaultWidth: {
    width: '100%',
    minWidth: 0,
  },
  helperText: {
    margin: 0,
    color: token.textSecondary,
  },
  helperTextError: {
    color: token.textDanger,
  },
});

const toNumber = (value: number | string | undefined): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
  return undefined;
};

type FplWeaveTextareaProps = Omit<TextAreaProps, 'size'> &
  Pick<FplTextareaProps, 'size' | 'rows' | 'expandable' | 'maxHeight'> & {
    /** CSS width, pre-extracted by `TextArea` from the legacy `sx.width`. */
    width?: string;
  };

const FplWeaveTextarea = forwardRef<HTMLDivElement, FplWeaveTextareaProps>(function FplWeaveTextarea(
  {
    id,
    label,
    helperText,
    error,
    placeholder,
    disabled,
    required,
    name,
    autoComplete,
    autoFocus,
    value,
    defaultValue,
    inputRef,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onPaste,
    onMouseDown,
    className,
    width,
    size,
    expandable,
    rows,
    maxHeight,
    'aria-label': ariaLabel,
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = helperText ? `${inputId}-helper-text` : undefined;

  // Value-first FPL onChange -> MUI event-first onChange. Typed as FPL's handler so it needs no cast.
  const handleChange: NonNullable<FplTextareaProps['onChange']> = (_value, opts) => {
    onChange?.(opts.event);
  };

  const domHandlers: DomHandlers = { onFocus, onBlur, onKeyDown, onPaste, onMouseDown } as unknown as DomHandlers;

  // Drop every interaction handler when disabled so the field behaves like MUI's `disabled`
  // (no focus/change/blur), unlike FPL's focusable read-only default.
  const interactionHandlers = disabled ? undefined : { onChange: handleChange, ...domHandlers };

  const hasExplicitWidth = Boolean(width);
  const fieldProps = stylex.props(styles.field, !hasExplicitWidth && styles.fieldDefaultWidth);
  const mergedClassName = [fieldProps.className, className].filter(Boolean).join(' ') || undefined;

  const fplProps: FplTextareaProps = {
    id: inputId,
    'aria-label': ariaLabel,
    'aria-describedby': helperId,
    size,
    name,
    placeholder,
    disabled,
    required,
    autoComplete,
    autoFocus,
    value: toStringValue(value),
    defaultValue: toStringValue(defaultValue),
    rows,
    expandable,
    maxHeight,
    ...interactionHandlers,
  };

  return (
    <div
      {...fieldProps}
      ref={ref}
      className={mergedClassName}
      style={width != null ? { ...fieldProps.style, width, minWidth: width } : fieldProps.style}
    >
      {label ? <FplLabel htmlFor={inputId}>{label}</FplLabel> : null}
      <FplTextarea ref={inputRef} {...fplProps} />
      {helperText ? (
        <p
          id={helperId}
          {...stylex.props(typographyStyles.textBodyMedium, styles.helperText, error && styles.helperTextError)}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
});

export const TextArea = forwardRef<HTMLDivElement, TextAreaProps>(function TextArea(props, ref) {
  const isFplEnabled = useFplMigrationFlag();

  if (isFplEnabled) {
    const { size = 'small', rows, minRows, maxRows, sx, ...restProps } = props;
    const width = toCssWidth(extractSxWidth(sx));
    const fplSize = size === 'small' ? 'md' : 'lg';
    const expandable = minRows != null || maxRows != null;
    const fplRows = expandable ? (toNumber(minRows) ?? 1) : (toNumber(rows) ?? DEFAULT_ROWS);
    // `maxRows` caps the autosized height at N lines.
    const maxHeight = typeof maxRows === 'number' ? `calc(${maxRows} * 1lh)` : undefined;

    return (
      <FplWeaveTextarea
        {...restProps}
        width={width}
        size={fplSize}
        expandable={expandable}
        rows={fplRows}
        maxHeight={maxHeight}
        ref={ref}
      />
    );
  }

  return <LegacyTextArea {...props} ref={ref} />;
});
